import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, Injectable, Signal, WritableSignal, effect, inject, signal } from '@angular/core';

import { Subscription, catchError, fromEvent, interval, merge, of, startWith } from 'rxjs';

import { AuthenticationApiService } from '../../api';
import { IDLE_TIMEOUT_MINUTES } from '../constants/auth.constants';
import { ActivityTrackerService } from './activity-tracker.service';
import { AuthService } from './auth.service';
import { DialogService } from './dialog.service';
import { SessionExpiryService } from './session-expiry.service';

export const IDLE_WARNING_DIALOG_ID = 'idle-warning';

const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
const IDLE_WARNING_MS = 2 * 60 * 1000;
const CHECK_INTERVAL_MS = 1000;
const IDLE_SOURCE = 'idle-timeout';

@Injectable({ providedIn: 'root' })
export class IdleLogoutService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly authService: AuthService = inject(AuthService);
  private readonly authApi: AuthenticationApiService = inject(AuthenticationApiService);
  private readonly activity: ActivityTrackerService = inject(ActivityTrackerService);
  private readonly dialog: DialogService = inject(DialogService);
  private readonly sessionExpiry: SessionExpiryService = inject(SessionExpiryService);
  private readonly remainingMsState: WritableSignal<number> = signal(IDLE_WARNING_MS);
  private subscription: Subscription | null = null;
  private signingOut = false;

  constructor() {
    effect(() => {
      if (this.authService.isAuthenticatedSignal()) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  public get remainingMs(): Signal<number> {
    return this.remainingMsState.asReadonly();
  }

  public stayLoggedIn(): void {
    this.activity.record();
    this.closeWarning();
    this.authApi
      .authControllerHeartbeat()
      .pipe(catchError((error: unknown) => of(error)))
      .subscribe({
        next: (result: unknown) => {
          if (result instanceof HttpErrorResponse && result.status === 401) {
            this.sessionExpiry.handle(result, IDLE_SOURCE);
          }
        },
        error: () => undefined,
      });
  }

  private start(): void {
    if (this.subscription) {
      return;
    }
    this.signingOut = false;
    this.activity.record();

    const visible$ = fromEvent(this.document, 'visibilitychange');

    this.subscription = merge(
      interval(CHECK_INTERVAL_MS),
      visible$,
      this.activity.otherTabActivity$,
    )
      .pipe(startWith(null))
      .subscribe({
        next: () => this.evaluate(),
        error: () => this.stop(),
      });
  }

  private stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.closeWarning();
  }

  private evaluate(): void {
    const idleMs = Date.now() - this.activity.lastActivityAt();
    const remainingMs = IDLE_TIMEOUT_MS - idleMs;

    if (remainingMs <= 0) {
      this.signOut();
      return;
    }
    if (remainingMs > IDLE_WARNING_MS) {
      this.closeWarning();
      return;
    }
    this.remainingMsState.set(remainingMs);
    this.activity.pause(true);

    if (!this.dialog.isOpen(IDLE_WARNING_DIALOG_ID)) {
      this.dialog.open(IDLE_WARNING_DIALOG_ID);
    }
  }

  private closeWarning(): void {
    this.activity.pause(false);

    if (this.dialog.isOpen(IDLE_WARNING_DIALOG_ID)) {
      this.dialog.close(IDLE_WARNING_DIALOG_ID);
    }
  }

  private signOut(): void {
    if (this.signingOut) {
      return;
    }
    this.signingOut = true;
    this.stop();

    this.authApi
      .authControllerSignout()
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: () => undefined,
        error: () => this.endLocally(),
        complete: () => this.endLocally(),
      });
  }

  private endLocally(): void {
    if (this.authService.isAuthenticatedSignal()) {
      this.sessionExpiry.end('session-expired', IDLE_SOURCE);
    }
  }
}
